import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../config.js';


const FooterContainer = styled.footer`
  background: ${props => props.theme.colors.surface};
  border-top: 1px solid ${props => props.theme.colors.border};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${props => props.theme.colors.primary}, transparent);
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 20px 0;
  
  @media (max-width: 768px) {
    padding: 16px 15px 0;
  }
`;

const FooterMain = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-bottom: 8px;
  }
`;

const MobileFooterRow = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    gap: 16px;
    min-height: 50px;
    background: rgba(0, 0, 0, 0.02);
    border-radius: 8px;
    margin-bottom: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 0;
    gap: 12px;
    min-height: 45px;
  }
  
  @media (max-width: 320px) {
    flex-direction: column;
    text-align: center;
    padding: 8px 0;
    gap: 8px;
    min-height: auto;
  }
`;

const MobileFooterColumn = styled.div`
  flex: 1;
  min-height: 44px;
  
  @media (max-width: 768px) {
    &.brand-column {
      flex: 2;
      text-align: left;
      
      .brand-logo {
        font-weight: 700;
        color: ${props => props.theme.colors.primary};
        margin-bottom: 6px;
        
        span {
          color: ${props => props.theme.colors.primary};
        }
      }
    }
    
    &.contact-column {
      flex: 1.5;
      text-align: right;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
  
  @media (max-width: 480px) {
    &.brand-column {
      .brand-logo {
        font-size: 0.9rem;
        margin-bottom: 4px;
      }
    }
    
    &.contact-column {
      flex: 1.2;
    }
  }
  
  @media (max-width: 320px) {
    &.brand-column, &.contact-column {
      flex: 1;
      text-align: center;
      justify-content: center;
      margin-bottom: 6px;
    }
    
    &.brand-column {
      margin-bottom: 8px;
    }
  }
`;

const FooterSection = styled(motion.div)`
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-bottom: 12px;
    position: relative;
    display: inline-block;
    padding-bottom: 8px;

    &::after {
      content: '';
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 20px;
      height: 2px;
      background: ${props => props.theme.colors.primary};
      border-radius: 1px;

      @media (max-width: 480px) {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  p {
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.4;
    margin-bottom: 8px;
    font-size: 0.9rem;
  }
  
  &.desktop-layout {
    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const BrandSection = styled(FooterSection)`
  .brand-logo {
    font-size: 1.3rem;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-bottom: 8px;
    
    span {
      color: ${props => props.theme.colors.primary};
    }
  }

  .brand-description {
    font-size: 0.8rem;
    line-height: 1.4;
    color: ${props => props.theme.colors.textSecondary};
    margin-bottom: 10px;
  }
  
  &.desktop-layout {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    grid-column: 1 / -1;
    text-align: center;
    margin-bottom: 10px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 4px;
  }
`;

const SocialLink = styled(motion.a)`
  width: 32px;
  height: 32px;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  min-width: 44px;
  min-height: 44px;

  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 4px;
  }

  a, button, span {
    color: ${props => props.theme.colors.textSecondary};
    text-decoration: none;
    transition: color 0.2s ease;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.9rem;
    padding: 4px 0;
    min-height: 32px;
    line-height: 1.5;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;

    &:hover {
      color: ${props => props.theme.colors.primary};
    }
    
    @media (max-width: 768px) {
      min-height: 28px;
      padding: 2px 0;
      font-size: 0.8rem;
    }
  }

  span {
    cursor: default;
  }
`;

const ContactInfo = styled.div`
  .contact-item {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    color: ${props => props.theme.colors.textSecondary};
    min-height: 32px;
    padding: 2px 0;

    @media (max-width: 480px) {
      justify-content: center;
    }

    .contact-icon {
      width: 20px;
      height: 20px;
      background: ${props => props.theme.colors.primary};
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 0.65rem;
      flex-shrink: 0;
    }

    .contact-text {
      font-size: 0.85rem;
      line-height: 1.2;
    }
    
    @media (max-width: 768px) {
      min-height: 24px;
      gap: 6px;
      margin-bottom: 4px;
      
      .contact-icon {
        width: 16px;
        height: 16px;
        font-size: 0.6rem;
      }
      
      .contact-text {
        font-size: 0.75rem;
      }
    }
  }
`;


const FooterBottom = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  
  @media (max-width: 768px) {
    padding: 8px 0;
    border-top: none;
  }
`;

const FloatingBackToTop = styled(motion.button)`
  position: fixed;
  bottom: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    bottom: 15px;
    left: 15px;
    font-size: 1.1rem;
  }
`;

const Copyright = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .heart {
    color: #ef4444;
    animation: heartbeat 1.5s ease-in-out infinite;
    margin: 0 2px;
    
    @media (max-width: 480px) {
      transform-origin: center;
      margin: 0 1px;
    }
  }

  @keyframes heartbeat {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;



const ModernFooter: React.FC = () => {
  const [contactSettings, setContactSettings] = useState({
    phone_number: '+91 7905333017',
    email_address: 'contact@drgurjar.com',
    location: 'Rajasthan, India'
  });

  const [siteSettings, setSiteSettings] = useState({
    site_title: '',
    site_description: ''
  });

  // Fetch contact and site settings from API
  useEffect(() => {
    const fetchContactSettings = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/public-contact-settings.php`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setContactSettings(data.data);
          }
        }
      } catch (error) {
        console.log('Failed to fetch contact settings, using defaults');
      }
    };

    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/public-site-settings.php`);
        if (response.data) {
          setSiteSettings({
            site_title: response.data.site_title || '',
            site_description: response.data.site_description || ''
          });
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };

    fetchContactSettings();
     fetchSiteSettings();
   }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <FooterContainer>
      <FooterContent>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <FooterMain>
            {/* Desktop/Tablet Layout */}
            <BrandSection variants={itemVariants} className="desktop-layout">
              <div className="brand-logo">
                Dr. <span>Ramhari</span>
              </div>

              <SocialLinks>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span>📘</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span>🐦</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span>💼</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <span>📷</span>
                </SocialLink>
              </SocialLinks>
            </BrandSection>

            <FooterSection variants={itemVariants} className="desktop-layout">
              <h3>Quick Links</h3>
              <FooterLinks>
                <li><button onClick={() => scrollToSection('slider')}>Home</button></li>
                <li><button onClick={() => scrollToSection('about')}>About Me</button></li>
                <li><button onClick={() => scrollToSection('services')}>Services</button></li>
                <li><button onClick={() => scrollToSection('blog')}>Blog</button></li>
              </FooterLinks>
            </FooterSection>

            <FooterSection variants={itemVariants} className="desktop-layout">
              <h3>Services</h3>
              <FooterLinks>
                <li><span>Health Consultation</span></li>
                <li><span>Farm Management</span></li>
                <li><span>Expert Training</span></li>
                <li><span>Biosecurity Solutions</span></li>
              </FooterLinks>
            </FooterSection>

            <FooterSection variants={itemVariants} className="desktop-layout">
              <h3>Contact Info</h3>
              <ContactInfo>
                <div className="contact-item">
                  <div className="contact-icon">
                    <span>📞</span>
                  </div>
                  <div className="contact-text">
                    {contactSettings.phone_number || '+91 7905333017'}
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <span>✉️</span>
                  </div>
                  <div className="contact-text">
                    {contactSettings.email_address || 'contact@drgurjar.com'}
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <span>📍</span>
                  </div>
                  <div className="contact-text">
                    {contactSettings.location || 'Rajasthan, India'}
                  </div>
                </div>
              </ContactInfo>
            </FooterSection>

            {/* Mobile Single-Row Overlay Layout */}
          <MobileFooterRow>
            <MobileFooterColumn className="brand-column">
              <div className="brand-logo" style={{ fontSize: '1rem', marginBottom: '4px' }}>
                {siteSettings.site_title}
              </div>
              <SocialLinks style={{ gap: '6px', justifyContent: 'flex-start' }}>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ width: '28px', height: '28px' }}
                >
                  <span>📘</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ width: '28px', height: '28px' }}
                >
                  <span>🐦</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ width: '28px', height: '28px' }}
                >
                  <span>💼</span>
                </SocialLink>
                <SocialLink
                  href="#"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{ width: '28px', height: '28px' }}
                >
                  <span>📷</span>
                </SocialLink>
              </SocialLinks>
            </MobileFooterColumn>
            <MobileFooterColumn className="contact-column">
              <ContactInfo style={{ gap: '4px', fontSize: '0.7rem', textAlign: 'right' }}>
                <div className="contact-item">
                  <div className="contact-icon" style={{ width: '14px', height: '14px' }}>
                    <span>📞</span>
                  </div>
                  <div className="contact-text">
                    {contactSettings.phone_number || '+91 7905333017'}
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon" style={{ width: '14px', height: '14px' }}>
                    <span>✉️</span>
                  </div>
                  <div className="contact-text">
                    {contactSettings.email_address || 'contact@drgurjar.com'}
                  </div>
                </div>
              </ContactInfo>
            </MobileFooterColumn>
          </MobileFooterRow>
          </FooterMain>
        </motion.div>

        <FooterBottom>
          <Copyright>
            <span>© 2024 {siteSettings.site_title}.</span>
            <span className="heart">❤️</span>
            <span>{siteSettings.site_description}</span>
          </Copyright>
        </FooterBottom>
      </FooterContent>
       
      <FloatingBackToTop
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        title="Back to top"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1 }}
      >
        <span>↑</span>
      </FloatingBackToTop>
    </FooterContainer>
  );
};

export default ModernFooter;